import { useEffect, useRef } from 'react';
import { FEATURES, STORAGE_KEYS } from '@/config/appConfig';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MessageInput } from './MessageInput';
import { ChatControls } from './ChatControls';
import { CategoryQueries } from './CategoryQueries';
import type { SuggestedQuery } from '../types/chat.types';
import { useTutorial } from '@/features/onboarding/useTutorial';

interface WelcomeScreenProps {
  input: string;
  onInputChange: (value: string) => void;
  onSendMessage: (value: string) => void;
  isLoading: boolean;
  onExportTxt: () => void;
  onExportPdf: () => Promise<void>;
  onCopyToClipboard: () => void;
}

export function WelcomeScreen({
  input,
  onInputChange,
  onSendMessage,
  isLoading,
  onExportTxt,
  onExportPdf,
  onCopyToClipboard,
}: WelcomeScreenProps) {
  const { startTutorial } = useTutorial();
  const { isLoadingProfile, appUser } = useAuth();
  const tutorialStartedRef = useRef(false);

  // Single switch for the whole suggestions bar below — flip to `false` in
  // features.ts to hide it without touching this component.
  const hasSuggestions = FEATURES.ONBOARDING_GUIDANCE;

  useEffect(() => {
    if (!FEATURES.ONBOARDING_TUTORIAL) return;
    if (localStorage.getItem(STORAGE_KEYS.tutorialSeen)) return;
    if (isLoadingProfile || !appUser) return;
    if (tutorialStartedRef.current) return;
    tutorialStartedRef.current = true;
    startTutorial();
  }, [isLoadingProfile, appUser, startTutorial]);

  const handleQuerySelect = (query: SuggestedQuery) => {
    onInputChange(query.template);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center md:px-4 px-0">
      <h1 className="mb-4 text-center text-2xl font-bold text-gray-700 md:mb-10 md:text-3xl">
        How can I help you today?
      </h1>
      <div className="w-full md:max-w-[80%]">
        <MessageInput
          value={input}
          onChange={onInputChange}
          onSend={onSendMessage}
          isLoading={isLoading}
          placeholder="Ask me anything about Phytobiotics products..."
        />
        <ChatControls
          onExportTxt={onExportTxt}
          onExportPdf={onExportPdf}
          onCopyToClipboard={onCopyToClipboard}
          onSendFeedback={() => {}}
          hasMessages={false}
          hasConversation={false}
        />
        {hasSuggestions && (
          <div className="mt-3">
            <CategoryQueries onSelect={handleQuerySelect} />
          </div>
        )}
      </div>
    </div>
  );
}
